import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { DtoTaskScene } from '@web-portal/domains/shared/domain-models';
import { buildAuthenticatedUrl } from 'utilities';

/**
 * ScenesAdapterService — Adapter for the BIM+ Scenes API.
 *
 * Calls the bimplus-websdk `scenes` namespace.
 */
@Injectable({
  providedIn: 'root',
})
export class ScenesAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);

  /**
   * Retrieves all scenes attached to a task/issue object.
   * Enriches each scene's thumbnail URL:
   * - New format: builds an authenticated URL from attachments[0].url + ?api-token={token}
   * - Old format: keeps the base64 data URI in thumbnail as-is
   *
   * Maps to: GET /v2/{team}/objects/{taskId}/scenes
   *
   * @param taskId - The GUID of the task/issue whose scenes to load.
   * @param cacheBust - When true, appends a cache-buster query parameter to the request URL.
   * @returns Observable of DtoTaskScene array with resolved thumbnail URLs.
   */
  getTaskScenes(taskId: string, cacheBust = false): Observable<DtoTaskScene[]> {
    const url = this.buildScenesUrl(`objects/${taskId}/scenes`, cacheBust);
    return this.api
      .rxRequest(this.api.request.get<DtoTaskScene[]>(url))
      .pipe(map(scenes => scenes.map(scene => this.enrichThumbnail(scene))));
  }

  /**
   * Retrieves a single scene by its ID.
   *
   * Maps to: GET /v2/{team}/scenes/{sceneId}
   *
   * @param sceneId - The GUID of the scene.
   * @param cacheBust - When true, appends a cache-buster query parameter to the request URL.
   * @returns Observable of DtoTaskScene.
   */
  getScene(sceneId: string, cacheBust = false): Observable<DtoTaskScene> {
    const url = this.buildScenesUrl(`scenes/${sceneId}`, cacheBust);
    return this.api
      .rxRequest(this.api.request.get<DtoTaskScene>(url))
      .pipe(map(scene => this.enrichThumbnail(scene)));
  }

  /**
   * Updates a scene by its ID with the provided data.
   *
   * Maps to: PUT /v2/{team}/scenes/{sceneId}
   *
   * @param sceneId - The GUID of the scene to update.
   * @param data - The scene data to update.
   * @returns Observable of the updated DtoTaskScene.
   */
  updateScene(sceneId: string, data: DtoTaskScene): Observable<DtoTaskScene> {
    return this.api
      .rxRequest(this.api.scenes.put(sceneId, JSON.stringify(data)))
      .pipe(map(scene => this.enrichThumbnail(scene)));
  }

  /**
   * Creates a new scene attached to a task/issue object.
   *
   * Maps to: POST /v2/{team}/objects/{taskId}/scenes
   *
   * @param taskId - The GUID of the task/issue to attach the scene to.
   * @param data - The scene data to create.
   * @returns Observable of the created DtoTaskScene.
   */
  createScene(taskId: string, data: Partial<DtoTaskScene>): Observable<DtoTaskScene> {
    return this.api
      .rxRequest(this.api.scenes.post(taskId, JSON.stringify(data)))
      .pipe(map(scene => this.enrichThumbnail(scene)));
  }

  /**
   * Deletes a scene by its ID.
   *
   * Maps to: DELETE /v2/{team}/scenes/{sceneId}
   *
   * @param sceneId - The GUID of the scene to delete.
   * @returns Observable that completes when the scene is deleted.
   */
  deleteScene(sceneId: string): Observable<void> {
    return this.api.rxRequest(this.api.scenes.delete(sceneId));
  }

  /**
   * Creates a thumbnail attachment for a scene.
   *
   * Maps to: POST /v2/{team}/scenes/{sceneId}/attachments
   *
   * @param sceneId - The GUID of the scene to attach the thumbnail to.
   * @param formData - FormData containing the thumbnail image.
   * @returns Observable of the attachment response, or null if upload fails.
   */
  createThumbnailAttachment(
    sceneId: string,
    formData: FormData
  ): Observable<Record<string, unknown> | null> {
    return (
      this.api.rxRequest(this.api.scenes.postThumbnailAttachment(sceneId, formData)) as Observable<
        Record<string, unknown>
      >
    ).pipe(
      catchError(error => {
        console.error('[ScenesAdapterService] Failed to create thumbnail attachment:', error);
        return of(null);
      })
    );
  }

  /**
   * Enriches a scene DTO with a resolved thumbnail URL using buildAuthenticatedUrl.
   *
   * New API format: scenes have an `attachments` array where `attachments[0].url`
   * is a relative path converted to a full authenticated URL.
   *
   * Old API format: `thumbnail` field is a base64 data URI — used as-is.
   */
  private enrichThumbnail(scene: DtoTaskScene): DtoTaskScene {
    const attachment = scene.attachments?.[0];
    if (attachment?.url) {
      const fullUrl = buildAuthenticatedUrl(
        this.api.getApiUrl(),
        attachment.url,
        this.api.getAccessToken()
      );
      return { ...scene, thumbnail: fullUrl, thumbnailUrl: fullUrl };
    }
    return scene;
  }

  private buildScenesUrl(path: string, cacheBust: boolean): string {
    const url = `${this.api.getApiTeamUrl()}${path}`;
    if (!cacheBust) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_=${Date.now()}`;
  }
}
